import {Storage} from './storage';
import {getPocketBase,getConnectionStatus} from './pocketbaseClient';
import {Client,ClientContact,Product,Invoice,BusinessSettings,ClientJob,AttachmentDocument,TimelineEvent,PaymentRecord,PdfSnapshot,EmailTemplate,EmailJob,RecurringProfile,TaxSettings,CurrencySettings,InvoiceDefaults,AutomationSettings,EmailLog,SmsLog,TermsTemplate,AutomationRun} from '../types';

type CollectionReference=
  | {type: 'pocketbase';collection: any}
  | {type: 'storage';key: string};

type ReadCacheEntry={value: any;expiresAt: number};
const READ_CACHE_TTL_MS=10000;
const readCache=new Map<string,ReadCacheEntry>();
const inFlightReads=new Map<string,Promise<any>>();
const readCacheVersions=new Map<string,number>();
let readCacheGeneration=0;

const cloneReadValue=(value: any): any=> Array.isArray(value) ? [...value] : value;

export const clearRepositoryReadCache=()=> {
  readCacheGeneration +=1;
  readCache.clear();
  inFlightReads.clear();
};

const invalidateRepositoryReadCache=(name: string)=> {
  readCacheVersions.set(name,(readCacheVersions.get(name) || 0) + 1);
  readCache.delete(name);
  inFlightReads.delete(name);
};

if (typeof window !=='undefined') {
  window.addEventListener('invoiceflow:refresh-data',clearRepositoryReadCache);
  window.addEventListener('invoiceflow:repository-cache-clear',clearRepositoryReadCache);
}

export const isPocketBaseMode=(): boolean=> {
  const {isMockMode}=getConnectionStatus();
  return !isMockMode;
};

const formatPocketBaseOperationError=(error: any,collectionName: string,operation: string): Error=> {
  const response=error?.response || {};
  const data=response?.data || {};
  const details=Object.entries(data)
    .map(([field,value]: [string,any])=> `${field}: ${String(value?.message || value?.code || JSON.stringify(value))}` )
    .join(';');
  const status=response?.status || error?.status || '';
  const message=String(response?.message || error?.message || 'Unknown PocketBase error');
  return new Error( `${operation} "${collectionName}" failed${status ? ` (${status})` : ''}: ${message}${details ? ` | ${details}` : ''}` );
};

const createRepository=<T>(name: string,defaultValue: T)=> ({
  name,
  getAll: async (): Promise<T>=> {
    const collection=await getCollection(name);
    if (collection.type==='pocketbase') {
      const now=Date.now();
      const cached=readCache.get(name);
      if (cached && cached.expiresAt > now) {
        return cloneReadValue(cached.value) as T;
      }
      const existingRead=inFlightReads.get(name);
      if (existingRead) {
        return cloneReadValue(await existingRead) as T;
      }
      const readGeneration=readCacheGeneration;
      const readVersion=readCacheVersions.get(name) || 0;
      const readPromise=readCollectionWithRetry(collection.collection)
        .then(records=> {
          const isStillCurrent=readCacheGeneration===readGeneration && (readCacheVersions.get(name) || 0)===readVersion;
          if (isStillCurrent) {
            readCache.set(name,{value: records,expiresAt: Date.now() + READ_CACHE_TTL_MS});
          }
          return records;
        })
        .finally(()=> {
          if (inFlightReads.get(name)===readPromise) inFlightReads.delete(name);
        });
      inFlightReads.set(name,readPromise);
      return cloneReadValue(await readPromise) as T;
    }
    return Storage.get(name,defaultValue) as T;
  },
  create: async (item: any): Promise<any>=> {
    const collection=await getCollection(name);
    if (collection.type==='pocketbase') {
      const {id,...itemWithoutId}=item;
      try {
        const created=await collection.collection.create(itemWithoutId);
        invalidateRepositoryReadCache(name);
        return created;
      } catch (error) {
        throw formatPocketBaseOperationError(error,name,'Create');
      }
    }
    const items=Storage.get(name,defaultValue) as any[];
    const newItem={...item,id: item.id || `${name}_${Date.now()}`};
    items.push(newItem);
    Storage.set(name,items);
    invalidateRepositoryReadCache(name);
    return newItem;
  },
  update: async (id: string,item: any): Promise<any>=> {
    const collection=await getCollection(name);
    if (collection.type==='pocketbase') {
      const {id: _id,...cleanItem}=item;
      try {
        const updated=await collection.collection.update(id,cleanItem);
        invalidateRepositoryReadCache(name);
        return updated;
      } catch (error) {
        throw formatPocketBaseOperationError(error,name,'Update');
      }
    }
    const items=Storage.get(name,defaultValue) as any[];
    const index=items.findIndex(i=> i.id===id);
    if (index >=0) {
      items[index]={...item,id};
      Storage.set(name,items);
    }
    invalidateRepositoryReadCache(name);
    return item;
  },
  delete: async (id: string): Promise<void>=> {
    const collection=await getCollection(name);
    if (collection.type==='pocketbase') {
      await collection.collection.delete(id);
      invalidateRepositoryReadCache(name);
      return;
    }
    const items=Storage.get(name,defaultValue) as any[];
    Storage.set(name,items.filter(i=> i.id !==id));
    invalidateRepositoryReadCache(name);
  }
});

const timelineRepository=createRepository<TimelineEvent[]>('timeline',[]);
const createTimelineRecord=timelineRepository.create;
timelineRepository.create=async (item: any): Promise<any>=> {
  try {
    return await createTimelineRecord(item);
  } catch (error) {
    const formatted=formatPocketBaseOperationError(error,'timeline','Create');
    console.error( 'Non-blocking timeline audit write failed:',formatted.message,item );
    const failures=Storage.get<any[]>('timeline_write_failures',[]);
    Storage.set( 'timeline_write_failures',[ ...failures,{...item,errorMessage: formatted.message,failedAt: new Date().toISOString()} ].slice(-100) );
    return {...item,id: `timeline_local_${Date.now()}`};
  }
};

const clientsRepository=createRepository<Client[]>('clients',[]);
const getUnsortedClients=clientsRepository.getAll;
clientsRepository.getAll=async (): Promise<Client[]>=> {
  const clients=await getUnsortedClients();
  return [...clients].sort((a,b)=> String(a.name || a.companyName || '').localeCompare( String(b.name || b.companyName || ''),undefined,{sensitivity: 'base'} ));
};

export const Repository={
  clients: clientsRepository,
  contacts: createRepository<ClientContact[]>('contacts',[]),
  products: createRepository<Product[]>('products',[]),
  invoices: createRepository<Invoice[]>('invoices',[]),
  payments: createRepository<PaymentRecord[]>('payments',[]),
  pdfSnapshots: createRepository<PdfSnapshot[]>('pdf_snapshots',[]),
  emailJobs: createRepository<EmailJob[]>('email_jobs',[]),
  recurringProfiles: createRepository<RecurringProfile[]>('recurring_profiles',[]),
  jobs: createRepository<ClientJob[]>('jobs',[]),
  attachments: createRepository<AttachmentDocument[]>('attachments',[]),
  timeline: timelineRepository,
  businessSettings: createRepository<any>('business_settings',null),
  termsTemplates: createRepository<TermsTemplate[]>('terms_templates',[]),
  emailTemplates: createRepository<EmailTemplate[]>('email_templates',[]),
  automationRuns: createRepository<AutomationRun[]>('automation_runs',[]),
  emailLogs: createRepository<EmailLog[]>('email_logs',[]),
  smsLogs: createRepository<SmsLog[]>('sms_logs',[]),
  businessEntities: createRepository<any[]>('business_entities',[]),
  businessDocuments: createRepository<any[]>('business_documents',[]),
  settings: {
    getBusiness: ()=> Storage.get<BusinessSettings>('business_profile',getNeutralBusinessDefaults()),
    getTax: ()=> Storage.get<TaxSettings>('tax_settings',getNeutralTaxDefaults()),
    getCurrency: ()=> Storage.get<CurrencySettings>('currency_settings',getNeutralCurrencyDefaults()),
    getInvoiceDefaults: ()=> Storage.get<InvoiceDefaults>('invoice_defaults',getNeutralInvoiceDefaults()),
    getAutomation: ()=> Storage.get<AutomationSettings>('automation_settings',getNeutralAutomationDefaults())
  }
};

export const loadSettings=async (): Promise<{business: BusinessSettings;tax: TaxSettings;currency: CurrencySettings;invoice: InvoiceDefaults;automation: AutomationSettings;}>=> {
  const neutralDefaults={
    business: getNeutralBusinessDefaults(),
    tax: getNeutralTaxDefaults(),
    currency: getNeutralCurrencyDefaults(),
    invoice: getNeutralInvoiceDefaults(),
    automation: getNeutralAutomationDefaults()
  };
  if (!isPocketBaseMode()) {
    return {
      business: Repository.settings.getBusiness(),
      tax: Repository.settings.getTax(),
      currency: Repository.settings.getCurrency(),
      invoice: Repository.settings.getInvoiceDefaults(),
      automation: Repository.settings.getAutomation()
    };
  }
  let settingsRecord: any | null=null;
  let lastError: Error | null=null;
  for (let attempt=0;attempt < 2;attempt++) {
    try {
      const allSettings=await Repository.businessSettings.getAll() as any[];
      settingsRecord=selectCanonicalSettingsRecord(allSettings);
      lastError=null;
      break;
    } catch (error) {
      lastError=error instanceof Error ? error : new Error('Unknown error');
      if (attempt===0) {
        await new Promise(resolve=> setTimeout(resolve,3000));
      }
    }
  }
  if (lastError && !settingsRecord) {
    throw new Error(`Failed to load cloud settings: ${lastError.message}`);
  }
  const business={...neutralDefaults.business,...(settingsRecord?.business || {})};
  const tax={...neutralDefaults.tax,...(settingsRecord?.tax || {})};
  const currency={...neutralDefaults.currency,...(settingsRecord?.currency || {})};
  const invoice={...neutralDefaults.invoice,...(settingsRecord?.invoice || {})};
  const automation={...neutralDefaults.automation,...(settingsRecord?.automation || {})};
  const payload={business,tax,currency,invoice,automation};
  hydrateSettingsCache(payload);
  return payload;
};

export interface WorkspacePreloadProgress {
  progress: number;
  title: string;
  detail: string;
}

export interface WorkspacePreloadResult {
  durationMs: number;
  stageTimings: Array<{title: string;durationMs: number}>;
}

export const preloadWorkspace=async (
  onProgress?: (progress: WorkspacePreloadProgress)=> void
): Promise<WorkspacePreloadResult>=> {
  const startedAt=Date.now();
  const stageTimings: Array<{title: string;durationMs: number}>=[];
  const stages=[
    {
      progress: 12,
      title: 'Loading business settings',
      detail: 'Verifying your company profile, invoice defaults and workspace preferences.',
      load: async ()=> {await loadSettings();}
    },
    {
      progress: 34,
      title: 'Syncing clients and catalogue',
      detail: 'Loading clients, contacts, products and job records in parallel.',
      load: async ()=> {await Promise.all([Repository.clients.getAll(),Repository.contacts.getAll(),Repository.products.getAll(),Repository.jobs.getAll()]);}
    },
    {
      progress: 56,
      title: 'Syncing invoices and payments',
      detail: 'Preparing invoice, payment and recurring billing records.',
      load: async ()=> {await Promise.all([Repository.invoices.getAll(),Repository.payments.getAll(),Repository.recurringProfiles.getAll()]);}
    },
    {
      progress: 76,
      title: 'Preparing documents and templates',
      detail: 'Loading invoice snapshots, attachments, email templates and business records.',
      load: async ()=> {
        await Promise.all([Repository.pdfSnapshots.getAll(),Repository.attachments.getAll(),Repository.emailTemplates.getAll()]);
        const optionalResults=await Promise.allSettled([Repository.businessEntities.getAll(),Repository.businessDocuments.getAll()]);
        optionalResults.forEach((result,index)=> {
          if (result.status==='rejected') {
            console.warn(`Optional Business Admin startup collection ${index + 1} could not be preloaded:`,result.reason);
          }
        });
      }
    },
    {
      progress: 92,
      title: 'Checking communications',
      detail: 'Loading email and SMS transmission records so delivery status is ready before you work.',
      load: async ()=> {await Promise.all([Repository.emailJobs.getAll(),Repository.emailLogs.getAll(),Repository.smsLogs.getAll()]);}
    }
  ];
  for (const stage of stages) {
    onProgress?.({progress: stage.progress,title: stage.title,detail: stage.detail});
    const stageStartedAt=Date.now();
    await stage.load();
    const durationMs=Date.now() - stageStartedAt;
    stageTimings.push({title: stage.title,durationMs});
    console.info(`[InvoiceFlow startup] ${stage.title}: ${durationMs}ms`);
  }
  const durationMs=Date.now() - startedAt;
  onProgress?.({progress: 100,title: 'Workspace ready',detail: 'All working records are prepared. Opening InvoiceFlow now.'});
  return {durationMs,stageTimings};
};

export const saveSettings=async (payload: any)=> {
  if (!isPocketBaseMode()) {
    hydrateSettingsCache(payload);
    return payload;
  }
  const allSettings=await Repository.businessSettings.getAll() as any[];
  const existing=selectCanonicalSettingsRecord(allSettings);
  const mergedSettings={
    business: {...getNeutralBusinessDefaults(),...(existing?.business || {}),...(payload.business || {})},
    tax: {...getNeutralTaxDefaults(),...(existing?.tax || {}),...(payload.tax || {})},
    currency: {...getNeutralCurrencyDefaults(),...(existing?.currency || {}),...(payload.currency || {})},
    invoice: {...getNeutralInvoiceDefaults(),...(existing?.invoice || {}),...(payload.invoice || {})},
    automation: {...getNeutralAutomationDefaults(),...(existing?.automation || {}),...(payload.automation || {})},
    updatedAt: new Date().toISOString()
  };
  const saved=existing ? await updateRecord(Repository.businessSettings,existing.id,mergedSettings) : await appendRecord(Repository.businessSettings,{...mergedSettings,createdAt: new Date().toISOString()});
  hydrateSettingsCache(saved);
  return saved;
};

export const verifyLiveBusinessSettings=(): boolean=> {
  if (!isPocketBaseMode()) return true;
  const business=Repository.settings.getBusiness();
  const businessName=String(business.businessName || '').trim();
  const businessEmail=String(business.email || '').trim();
  return !!(businessName && businessEmail);
};

export const appendRecord=async (repository: any,item: any): Promise<any>=> {
  return await repository.create(item);
};

export const updateRecord=async (repository: any,id: string,item: any): Promise<any>=> {
  return await repository.update(id,item);
};

const selectCanonicalSettingsRecord=(records: any[]): any | null=> {
  if (!Array.isArray(records) || records.length===0) return null;
  return [...records].sort((a,b)=> {
    const aTime=new Date(a.updatedAt || a.updated || a.createdAt || a.created || 0).getTime();
    const bTime=new Date(b.updatedAt || b.updated || b.createdAt || b.created || 0).getTime();
    return bTime - aTime;
  })[0];
};

const hydrateSettingsCache=(settings: any)=> {
  if (settings.business) Storage.set('business_profile',settings.business);
  if (settings.tax) Storage.set('tax_settings',settings.tax);
  if (settings.currency) Storage.set('currency_settings',settings.currency);
  if (settings.invoice) Storage.set('invoice_defaults',settings.invoice);
  if (settings.automation) Storage.set('automation_settings',settings.automation);
};

const getCollection=async (name: string): Promise<CollectionReference>=> {
  const pb=await getPocketBase().catch(()=> null);
  const {isMockMode}=getConnectionStatus();
  if (isMockMode) return {type: 'storage',key: name};
  if (!pb) throw new Error(`PocketBase connection failed for collection "${name}".`);
  return {type: 'pocketbase',collection: pb.collection(name)};
};

const readCollectionWithRetry=async (collection: any,attempt=0): Promise<any>=> {
  try {
    return await collection.getFullList();
  } catch (error) {
    if (attempt < 1) {
      await new Promise(resolve=> setTimeout(resolve,800));
      return readCollectionWithRetry(collection,attempt + 1);
    }
    throw new Error(`Collection read failed: ${(error as Error).message}`);
  }
};

const getNeutralBusinessDefaults=(): BusinessSettings=> ({id: 'default',businessName: '',tradingName: '',abn: '',addressLine1: '',addressLine2: '',suburb: '',state: '',postcode: '',country: 'Australia',email: '',phone: '',website: '',logoUrl: '',logoDataUrl: ''});
const getNeutralTaxDefaults=(): TaxSettings=> ({gstRegistered: true,defaultGstRate: 0.10,defaultTaxTreatment: 'gst_10',customTaxRate: 0});
const getNeutralCurrencyDefaults=(): CurrencySettings=> ({defaultCurrency: 'AUD',supportedCurrencies: ['AUD','USD'],decimalPlaces: 2});
const getNeutralInvoiceDefaults=(): InvoiceDefaults=> ({prefix: 'INV',nextNumber: 1001,dueDays: 14,paymentTerms: '',paymentTermsHtml: '',bankName: '',bsb: '',accountNumber: '',paymentReferenceFormat: 'invoice_number',defaultPublicNote: '',defaultInternalNote: '',autoWorkflow: {saveInvoice: true,generatePdf: true,queueEmail: true},pdfAccentColor: '#0f766e',payIdEnabled: false,payId: '',payIdName: ''});
const getNeutralAutomationDefaults=(): AutomationSettings=> ({fromName: '',replyToEmail: '',defaultCc: '',defaultBcc: '',permanentAuditCc: '',permanentAuditBcc: '',provider: 'none',sendingMode: 'disabled',pollingFrequencyMinutes: 0,accountsContactName: '',accountsContactTitle: '',accountsContactEmail: '',accountsContactPhone: '',invoiceEmailSubjectTemplate: '',invoiceEmailBodyTemplate: '',recipientBehavior: {useClientBilling: true,includeContacts: true,allowManual: true}});